import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Form, useFormState, useForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import {
  Button,
} from '@unite-us/app-components';
import _ from 'lodash';
import { connect } from 'react-redux';
import { OverlaySpinner } from 'common/spinners';
import callOrLog from 'src/common/utils/callOrLog';
import { FACESHEET } from 'common/utils/EventTracker/utils/eventConstants';
import { generateUUID } from 'common/utils/utils';
import AddressFieldGroup from 'common/form/AddressField/AddressFieldGroup';
import { deleteGroupLocation } from 'actions/Group';
import { isAddressValid } from 'common/form/Profile/utils';

import './stylesheets/editAddress.scss';

function AddressFieldList({
  formAddresses,
  styles,
  removeOrDelete,
  isValidAddress,
}) {
  return formAddresses.map((address, index) => (
    <div
      id={`address-${index}`}
      key={address.id || index}
      style={styles.inlineAddressContainer}
    >
      <AddressFieldGroup
        id={address.id}
        fieldPrefix={`addresses[${index}].attributes`}
        forceRequired
        inline={false}
        remove={() => removeOrDelete(address.id)}
        isValidAddress={isValidAddress}
      />
    </div>
  ));
}

AddressFieldList.propTypes = {
  formAddresses: PropTypes.array.isRequired,
  styles: PropTypes.shape({
    inlineAddressContainer: PropTypes.object.isRequired,
  }).isRequired,
  removeOrDelete: PropTypes.func.isRequired,
  isValidAddress: PropTypes.func.isRequired,
};

function AddressFormActions({
  addField,
  closeModal,
  submitting,
  hasInvalidAddress,
  onSave,
}) {
  return (
    <div className="actions">
      <div className="row">
        <div className="col-sm-6 text-left edit-address__add-address">
          <a
            id="add-address-link"
            role="button"
            onClick={addField}
          >
            + Add Address
          </a>
        </div>

        <div className="col-sm-6">
          <span className="action-item">
            <Button
              id="edit-address-cancel-btn"
              onClick={closeModal}
              disabled={submitting}
              label="Cancel"
            />
          </span>
          <span className="action-item">
            <Button
              id="edit-address-save-btn"
              onClick={onSave}
              disabled={submitting || hasInvalidAddress}
              label="Save"
              primary
            />
          </span>
        </div>
      </div>
    </div>
  );
}

AddressFormActions.propTypes = {
  addField: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  hasInvalidAddress: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
};

// Inner component rendered inside <Form> so that useFormState/useForm have context
function AddressFormInner({
  closeModal,
  deleteGroupLocationAction,
  styles,
}) {
  const form = useForm();
  const { values, submitting } = useFormState({
    subscription: { values: true, submitting: true },
  });
  const [deleting, setDeleting] = React.useState(false);
  const [hasInvalidAddress, setHasInvalidAddress] = React.useState(false);
  const [newIds, setNewIds] = React.useState([]);

  const formAddresses = _.get(values, 'addresses', []);

  const isValidAddress = React.useCallback(async (location) => {
    const valid = await isAddressValid(location);
    setHasInvalidAddress(!valid);
    return !valid;
  }, []);

  const addField = React.useCallback(() => {
    const newId = generateUUID();
    form.mutators.push('addresses', {
      id: newId,
      attributes: {
        line_1: '',
        line_2: '',
        city: '',
        county: '',
        state: '',
        postal_code: '',
        country: '',
      },
    });
    setNewIds((prev) => [...prev, newId]);
  }, [form]);

  const removeUnsavedAddress = React.useCallback((idToRemove) => {
    const index = _.findIndex(formAddresses, (a) => a.id === idToRemove);
    if (index >= 0) {
      form.mutators.remove('addresses', index);
    }
    setNewIds((prev) => _.filter(prev, (id) => id !== idToRemove));
  }, [formAddresses, form]);

  const deleteSavedAddress = React.useCallback((idToRemove) => {
    setDeleting(true);

    const filteredAddresses = _.filter(formAddresses, (address) => (
      address.id !== idToRemove && !_.includes(newIds, address.id)
    ));

    const addressValues = _.map(filteredAddresses, (address) => ({
      id: address.id,
      line_1: _.get(address, 'attributes.line_1', ''),
      line_2: _.get(address, 'attributes.line_2', ''),
      city: _.get(address, 'attributes.city', ''),
      county: _.get(address, 'attributes.county', ''),
      state: _.get(address, 'attributes.state', ''),
      postal_code: _.get(address, 'attributes.postal_code', ''),
      country: _.get(address, 'attributes.country', ''),
    }));

    if (!_.isEmpty(addressValues)) {
      callOrLog(() => window.eventTracker(FACESHEET.addressUpdated));
    }

    deleteGroupLocationAction(idToRemove)
      .then(() => {
        const index = _.findIndex(formAddresses, (a) => a.id === idToRemove);
        if (index >= 0) {
          form.mutators.remove('addresses', index);
        }
        setDeleting(false);
      });
  }, [formAddresses, newIds, deleteGroupLocationAction, form]);

  const removeOrDelete = React.useCallback((idToRemove) => {
    if (_.includes(newIds, idToRemove)) {
      removeUnsavedAddress(idToRemove);
    } else {
      deleteSavedAddress(idToRemove);
    }
  }, [newIds, removeUnsavedAddress, deleteSavedAddress]);

  return (
    <form
      className="edit-address content-with-actions"
      onSubmit={(e) => { e.preventDefault(); form.submit(); }}
    >
      <OverlaySpinner text="Saving Addresses..." show={submitting || deleting} />

      <div className="content-container">
        <AddressFieldList
          formAddresses={formAddresses}
          styles={styles}
          removeOrDelete={removeOrDelete}
          isValidAddress={isValidAddress}
        />
      </div>

      <AddressFormActions
        addField={addField}
        closeModal={closeModal}
        submitting={submitting}
        hasInvalidAddress={hasInvalidAddress}
        onSave={() => form.submit()}
      />
    </form>
  );
}

AddressFormInner.propTypes = {
  closeModal: PropTypes.func.isRequired,
  deleteGroupLocationAction: PropTypes.func.isRequired,
  styles: PropTypes.shape({
    inlineAddressContainer: PropTypes.object.isRequired,
  }).isRequired,
};

// Outer component that sets up the RFF <Form> and renders AddressFormInner inside it
function AddressFormContent({
  addresses,
  closeModal,
  deleteGroupLocation: deleteGroupLocationAction,
  onSave,
  styles,
}) {
  const onSubmit = React.useCallback((formValues) => {
    const submittedAddresses = formValues.addresses;

    if (!_.isEmpty(submittedAddresses)) {
      callOrLog(() => window.eventTracker(FACESHEET.addressUpdated));
    }

    return onSave(submittedAddresses)
      .then(() => {
        closeModal();
      });
  }, [onSave, closeModal]);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{ addresses }}
      mutators={{ ...arrayMutators }}
      render={() => (
        <AddressFormInner
          closeModal={closeModal}
          deleteGroupLocationAction={deleteGroupLocationAction}
          styles={styles}
        />
      )}
    />
  );
}

AddressFormContent.propTypes = {
  addresses: PropTypes.array.isRequired,
  closeModal: PropTypes.func.isRequired,
  deleteGroupLocation: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  styles: PropTypes.shape({
    inlineAddressContainer: PropTypes.object.isRequired,
  }).isRequired,
};

// Wrapper component that receives modalIsOpen/closeModal from EditModal
export class EditAddressesGroup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      renderKey: 0,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // Re-initialize form when modal opens by changing the key
    if (!this.props.modalIsOpen && nextProps.modalIsOpen) {
      this.setState((prev) => ({ renderKey: prev.renderKey + 1 }));
    }
  }

  render() {
    const {
      addresses,
      closeModal,
      deleteGroupLocation: deleteGroupLocationAction,
      onSave,
      styles,
    } = this.props;

    return (
      <AddressFormContent
        key={this.state.renderKey}
        addresses={addresses}
        closeModal={closeModal}
        deleteGroupLocation={deleteGroupLocationAction}
        onSave={onSave}
        styles={styles}
      />
    );
  }
}

EditAddressesGroup.propTypes = {
  addresses: PropTypes.array.isRequired,
  closeModal: PropTypes.func.isRequired,
  deleteGroupLocation: PropTypes.func.isRequired,
  modalIsOpen: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  styles: PropTypes.shape({
    inlineAddressContainer: PropTypes.object.isRequired,
  }).isRequired,
};

EditAddressesGroup.defaultProps = {
  styles: {
    inlineAddressContainer: {
      borderBottom: '1px solid #EBECED',
      marginBottom: '20px',
      paddingBottom: '10px',
    },
  },
};

EditAddressesGroup.contextTypes = {
  eventTracker: PropTypes.func.isRequired,
};

function mapStateToProps(state, ownProps) {
  const { addresses } = ownProps;
  const groupId = _.get(state, 'session.groupId');

  return {
    initialAddresses: addresses,
    groupId,
  };
}

export default connect(
  mapStateToProps,
  { deleteGroupLocation },
)(EditAddressesGroup);
